/**
 * ActionChips — canonical follow-up chip row for all presentations.
 *
 * Replaces 9× duplicated FollowChips / ActionChips definitions.
 * Chips may be strings or {id, label, icon} objects.
 *
 * Props:
 *   chips       — string[] | {id, label, icon}[]
 *   onAction    — (id: string) => void
 *   accentColor — override accent color (default: blue #3B82F6)
 *   style       — container style override
 */
import React, { useState } from 'react';
import { base } from '../tokens/colors';

const DEF_ACCENT     = '#3B82F6';
const DEF_ACCENT_SOFT = 'rgba(59,130,246,0.08)';
const DEF_ACCENT_BDR  = 'rgba(59,130,246,0.35)';

function hex2rgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function ActionChips({ chips, onAction, accentColor, style }) {
  const [hovIdx, setHovIdx] = useState(null);
  if (!chips || chips.length === 0) return null;

  const accent    = accentColor || DEF_ACCENT;
  const accentRaw = accent.startsWith('#') && accent.length === 7;
  const accentSoft = accentRaw ? hex2rgba(accent, 0.08) : DEF_ACCENT_SOFT;
  const accentBdr  = accentRaw ? hex2rgba(accent, 0.35) : DEF_ACCENT_BDR;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, ...style }}>
      {chips.map((chip, i) => {
        const label = typeof chip === 'string' ? chip : chip.label;
        const id    = typeof chip === 'string' ? chip : (chip.id || chip.label);
        const icon  = typeof chip === 'object' ? chip.icon : null;
        const hov   = hovIdx === i;
        return (
          <button
            key={i}
            onClick={() => onAction && onAction(`follow:${id}`)}
            onMouseEnter={() => setHovIdx(i)}
            onMouseLeave={() => setHovIdx(null)}
            onFocus={() => setHovIdx(i)}
            onBlur={() => setHovIdx(null)}
            aria-label={label}
            style={{
              display:    'inline-flex',
              alignItems: 'center',
              gap:        6,
              height:     36,
              padding:    '0 16px',
              borderRadius: 20,
              background: hov ? accentSoft : 'rgba(255,255,255,0.03)',
              border:     `1px solid ${hov ? accentBdr : base.border}`,
              color:      hov ? accent : base.textSec,
              fontSize:   12.5,
              cursor:     'pointer',
              transition: 'all 0.15s ease',
              outline:    'none',
              fontFamily: 'inherit',
            }}
          >
            {icon && <span style={{ fontSize: 13 }}>{icon}</span>}
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default ActionChips;
